import { useState, useEffect } from 'react';
import Modal from '@src/common/Modal';
import ReactSelect from '@common/ReactSelect';
import * as HELPER from "@src/config/helper";
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux'
import { savePaymentInfo } from '@src/store/slices/insurance';
import moment from 'moment';
import { Calendra2 } from "@common/DatePicker";
import { getMasterConfigID } from '@src/store/slices/common';
import { MAX_PAYMENT_FILE_UPLOAD } from '@src/config/id/constant';
import { getDocUploadUrl } from '../../../store/slices/common'; 
import CurrencyInputField from '@common/CurrencyInputField';
import { removeCurrencyFormattingId } from '@src/config/helper';
import FullPageLoader from '@common/FullPageLoader';
import { useLanguage } from "@src/context/LanguageContext";
import { Accordion } from 'react-bootstrap';
import { isMobile } from "react-device-detect";
import OcrImg  from "../../../styles/webroot/images/ocr-img.png";


/**
 * PaymentInfoID Component
 * Modal component for capturing and submitting payment information for insurance leads.
 * Handles payment details, file uploads, validation, and submission.
 */
const PaymentInfoID = (props: any) => {
    const { show, handleClose, disableField, leadDetail } = props;
    const commonState = useSelector((state: any) => state.common);
    const {translations} = useLanguage();


    const [formData, setFormData] = useState<any>({
        payment_from: '',
        payment_method: '',
        premium_amount: '',
        pending_amount: '',
        date_time: '',
        payment_ref_no: '',
        images: []
    });

    const [previewImages, setPreviewImages] = useState<any[]>([]);
    const [errors, setErrors] = useState<any>({});
    const [loading, setLoading] = useState(false)
    const dispatch: any = useDispatch();
    
    // Payment options from master configuration
    const paymentFromOptions = commonState?.common?.payment_from || [];
    const paymentMethodOptions = commonState?.common?.payment_method || [];

    // Fetch master configuration on component mount if not already loaded
    useEffect(() => {
        if (!commonState?.common || paymentFromOptions.length === 0 || paymentMethodOptions.length === 0) {
            dispatch(getMasterConfigID());
        }
    }, [commonState?.common, paymentFromOptions.length, paymentMethodOptions.length, dispatch]);

    /**
     * Handles form field changes for different input types
     * Supports ReactSelect, CurrencyInputField, and regular input fields
     * Automatically clears validation errors for the changed field
     */
    const handleChange = (name: string, event: any) => {
        let finalValue: any = '';

        // ReactSelect
        if (event && event.value && !event.target) {
            finalValue = event.value;
        }
        // Handle regular input or CurrencyInputField (has target with value)
        else if (event && event.target && 'value' in event.target) {
            if (name === 'payment_ref_no') {
                finalValue = event.target.value;
            } else {
                finalValue = removeCurrencyFormattingId(event.target.value);
            }
        }
        // fallback
        else {
            finalValue = event;
        }

        setFormData((prev: any) => ({
            ...prev,
            [name]: finalValue,
        }));

        // Clear error for this field when user starts typing
        setErrors((prev: any) => ({
            ...prev,
            [name]: '',
        }));
    };


    const handleDateChange = (date: Date | null) => {
        setErrors((prev: any) => ({
            ...prev,
            date_time: ''
        }));

        setFormData((prev: any) => ({
            ...prev,
            date_time: date ? date.toString() : ''
        }));
    };

    const handleFileInputChange = async (e: any) => {
        let selectedFiles = e.target.files;
        if (!selectedFiles || selectedFiles.length === 0) return;
        
        let validFiles: File[] = [];
        let imagePreview: any[] = [];

        if ((selectedFiles.length + previewImages.length) > MAX_PAYMENT_FILE_UPLOAD) {
            toast.error("Maximum 2 Documents are allowed");
            return;
        }

        for (const file of selectedFiles) {
            let allowedExtensions = ['data:application/pdf', 'data:image/*'];
            let base64PreviewImg: any = await HELPER.toBase64(file);
            let filetype = await HELPER.detectMimeType(base64PreviewImg, { allowedExtensions });

            if (!filetype) {
                toast.error("Please select a valid file");
            } else {
                validFiles.push(file);
                imagePreview.push({
                    url: base64PreviewImg,
                    type: file.type.split('/')[1],
                    file: file
                });
            }
        }

        if (validFiles.length > 0) {
            setFormData((prev: any) => ({
                ...prev,
                images: [...prev.images, ...validFiles]
            }));
            setPreviewImages((prev: any) => [...prev, ...imagePreview]);
        }

        e.target.value = '';
        setErrors((prev: any) => ({ ...prev, images: '' }));
    };

    const removeImage = (index: number) => {
        setPreviewImages((prev) => prev.filter((_, i) => i !== index));
        setFormData((prev: any) => ({
            ...prev,
            images: prev.images.filter((_: any, i: number) => i !== index)
        }));
    };


    const uploadPaymentProofImages = async (files: File[]) => {
        try {
            if (!files || files.length === 0) return [];

            const uploadedUrls: string[] = [];

            // Upload each file sequentially
            for (const file of files) {
                const formData = new FormData();
                formData.append('upload_type', 'insurance_docs');
                formData.append('upload_doc_type', 'insurance_docs');
                formData.append('lead_id', leadDetail?.leadId);
                formData.append('files', file);

            const res: any = await getDocUploadUrl(formData);

                if (res?.status === 200 && res?.data && res?.data[0]?.file_url) {
                    uploadedUrls.push(res.data[0].file_url);
                } else {
                    toast.error('Payment proof upload failed');
                    return [];
                }
            }

            return uploadedUrls;
        } catch (error) {
            toast.error('Error while uploading payment proof');
            return [];
        }
    };

    /**
     * Validates all required form fields
     * Sets error messages for invalid fields
     * Returns true if all validations pass, false otherwise
     */
    const isFormValid = async (data: any) => {
        //return false;
        let isValid = true;
        let updatedError: any = {};

        // Validate Payment From
        if (!data.payment_from) {
            updatedError.payment_from = translations?.GENERATE_PAYMENT?.FROM_REQUIRED;
            isValid = false;
        }

        // Validate Payment Method
        if (!data.payment_method) {
            updatedError.payment_method = translations?.GENERATE_PAYMENT?.METHOD_REQUIRED;
            isValid = false;
        }

        // Validate Premium Amount (must be greater than 0)
        if (!data.premium_amount || Number(data.premium_amount) <= 0) {
            updatedError.premium_amount = translations?.GENERATE_PAYMENT?.PRE_AMT_REQUIRED;
            isValid = false;
        }

        // Validate Payment Date
        if (!data.date_time) {
            updatedError.date_time = translations?.GENERATE_PAYMENT?.PRE_PAID_DATE_REQUIRED;
            isValid = false;
        }

        // Validate Payment Proof Documents (at least one required)
        if (!data.images || data.images.length === 0) {
            updatedError.images =  translations?.GENERATE_PAYMENT?.UPLOAD_DOC_REQUIRED;
            isValid = false;
        }

        setErrors({ ...updatedError });
        return isValid;
    }

    /**
     * Handles form submission
     * Validates form, uploads payment proof documents, and saves payment information
     */
    const handleSubmit = async () => {
        setLoading(true);
        
        if (await isFormValid(formData)) {
            try {
                // Upload payment proof images first
                const uploadedPaymentProofUrls = await uploadPaymentProofImages(
                    formData.images
                );

                // If upload fails, stop submission
                if (formData.images.length > 0 && uploadedPaymentProofUrls.length === 0) {
                    setLoading(false);
                    return;
                }

                // Prepare request payload
                const requestPayload = {
                    payment_from: formData?.payment_from,
                    payment_method: formData?.payment_method,
                    payment_amount: formData?.premium_amount,
                    amount_date: formData?.date_time ? moment(formData?.date_time).format('YYYY-MM-DD') : '',
                    payment_ref_no: String(formData?.payment_ref_no || ''),
                    upload_payment_proof: uploadedPaymentProofUrls,
                    lead_id: leadDetail?.leadId
                };

                // Save payment information
                await dispatch(savePaymentInfo(requestPayload)).then((res: any) => {
                    if (res?.status === 200) {
                        setLoading(false);
                        toast.success(res?.message);
                        handleClose();
                    } else {
                        handleClose();
                        setLoading(false);
                    }
                }).catch((error: any) => {
                    setLoading(false);
                    handleClose();
                });
            } catch (error) {
                setLoading(false);
            }
        } else {
            setLoading(false);
            toast.error( translations?.GENERATE_PAYMENT?.ALL_FIELD_REQ);
        }
    };

    useEffect(() => {
        if (show) document.body.classList.add("overflow-hidden");
        return () => {
            document.body.classList.remove("overflow-hidden");
        }
    }, [show])
    

    return (
        <div className="model-outer upload-error-popup Payment-info-popup-id">
            <FullPageLoader show={loading} />
            <Modal show={show} handleClose={handleClose}>
                <div className='modal-header'>
                     <h2>Payment info</h2>
                </div>
                <div className="modal-body">

                    <div className="row">
                   

                        <div className="form-select-bx col-md-6 col-xs-12">
                            <ReactSelect
                                options={paymentFromOptions}
                                value={ paymentFromOptions.find( (v: any) => v.value === formData.payment_from ) || ''}
                                handleChange={(e: any) => handleChange('payment_from', e)}
                                placeholder={`${translations?.GENERATE_PAYMENT?.FROM} *`}
                                fieldKey="payment_from"
                                errorField={errors?.payment_from}
                            />
                        </div>

                        <div className="form-select-bx col-md-6 col-xs-12">
                            <ReactSelect
                                options={paymentMethodOptions}
                                value={ paymentMethodOptions.find((v: any) => v.value === formData.payment_method) || ''}
                                handleChange={(e: any) => handleChange('payment_method', e)}
                                placeholder={`${translations?.GENERATE_PAYMENT?.Method} *`}
                                fieldKey="payment_method"
                                errorField={errors?.payment_method}
                            />
                        </div>
                    
                        <fieldset className="col-md-6 col-xs-12">
                            <div className="material">
                                <input
                                    spellCheck={false}
                                    type="text"
                                    placeholder=""
                                    className="form-input"
                                    value={HELPER.addCurrencyFormatting(formData.premium_amount)}
                                    onChange={(e) => handleChange('premium_amount', e)}
                                />
                                <label
                                    className="form-label"
                                    data-label={translations?.GENERATE_PAYMENT?.PRE_AMT}
                                ></label>
                                 {errors.premium_amount && (
                                        <span className="error-txt">
                                            {errors.premium_amount || ""}
                                        </span>
                                    )}
                            </div>
                        </fieldset>

                          <fieldset className="form-filed date-picker-detail col-md-6 col-xs-12">
                            <div className={formData?.date_time ? 'material animation-effect active-label' : 'material animation-effect'}>
                                <Calendra2
                                    onChange={(date: Date) => handleDateChange(date)}
                                    placeholder= {`${translations?.GENERATE_PAYMENT?.DATE_TIME} *`}
                                    dateFormat="dd/MM/yyyy"
                                    selected={formData.date_time}
                                    disabled={disableField}
                                    fieldLabel="Payment Date"
                                />
                                 {errors.date_time && (
                                        <span className="error-txt">
                                            {errors.date_time || ""}
                                        </span>
                                    )}
                                
                            </div>
                        </fieldset>
                    
                        <fieldset className="col-md-12">
                            <div className="material">
                                <input
                                    type="text"
                                    placeholder=" "
                                    className="form-input"
                                    value={formData.payment_ref_no}
                                    onChange={(e) => handleChange('payment_ref_no', e)}
                                />
                                <label
                                    className="form-label"
                                    data-label={translations?.GENERATE_PAYMENT?.REF_NO}
                                ></label>
                            </div>
                        </fieldset>
                    </div>

<div className='row'>

<div className="col-md-12 col-xs-12 faq-wrapper resummison-outer document-upload-acc-outer w-100">
  <div className="document-card animation-effect">

    <Accordion
      defaultActiveKey="0"
      className="accordian-outer config-accordain"
    >
      <Accordion.Item eventKey="0">

        {/* HEADER */}
        <div className="summery-heading upload-heading-text document-upload-id payment-info-acc">
          <Accordion.Header>
            {
            //   isMobile  && 
              <span className="doc-upload-icn"><img src={OcrImg} className="" alt="" /></span> 

                                    }

          
            <h3>{translations?.GENERATE_PAYMENT?.PROOF}</h3>

          </Accordion.Header>
        </div>

        <Accordion.Body>

          <div className="upload-proof-box mt-4">

            <div className='row'>
             

              {previewImages.map((img, index) => (
                <div className="img-bx-select-opt col-md-6 payment-info-id" key={index}>
                  <div className="image-bx more-file-upload add-insurance-partner image-container-agency">
                    <span className="close-icn" onClick={() => removeImage(index)}>
                      <i className="ic-clearclose"></i>
                    </span>

                    <div className="document-image-container">
                      {
                        img?.type?.includes('pdf')
                          ? <i className="ic-pdf-file"></i>
                          : <img src={img.url} alt="doc" />
                      }
                    </div>
                  </div>
                </div>
              ))}

              <div className='col-md-6 col-xs-6 p-lg-t'>
                {previewImages.length < MAX_PAYMENT_FILE_UPLOAD && (
                  <div className="add-document-proof">
                    <input
                      type="file"
                      className="upload-image-input"
                      multiple
                      onChange={handleFileInputChange}
                      disabled={disableField}
                    />
                    <i className="ic-add"></i>
                    <span className='add-doc-text d-block'>
                      {translations?.GENERATE_PAYMENT?.ADD_DOC}
                    </span>
                  </div>
                )}
              </div>
            
             <div className='warning-txt col-md-12'>
                <i className='ic-warning m-xs-r'></i>
               <span> {translations?.GENERATE_PAYMENT?.MSG_ACTIVE}</span>
            </div> 

            </div>

          </div>
          {errors.images && (
            <span className="error-txt">
              {errors.images}
            </span>
          )}

        </Accordion.Body>

      </Accordion.Item>
    </Accordion>
  </div>
</div>
</div>

                    
                </div>
                <div className="modal-footer br-top justify-center">
                        <button className="btn-primary m-lg-t" onClick={handleSubmit}> {translations?.COMMON_MSG?.SUBMIT}</button>
                    </div>
            </Modal>
        </div>
    );
};

export default PaymentInfoID;
